import { useCallback, useEffect, useRef, type RefObject } from "react";
import { isPlaybackSpaceBlocked } from "../Video/usePlaybackSpaceShortcut";

// Text entry keeps its focus: Space is typing there, and a pointer click is how it gets started.
const TEXT_ENTRY_SELECTOR = [
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"])',
  "textarea",
  "select",
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(", ");

/** Return after a committed action, without reclaiming a newer focus/navigation intent. */
export function useTimelineActionFocus(
  rootRef: RefObject<HTMLElement | null>,
  focusTimeline: () => void,
) {
  const generationRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const cancel = useCallback(() => {
    generationRef.current += 1;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);
  useEffect(() => {
    const doc = rootRef.current?.ownerDocument;
    doc?.addEventListener("pointerdown", cancel, true);
    doc?.addEventListener("keydown", cancel, true);
    return () => {
      cancel();
      doc?.removeEventListener("pointerdown", cancel, true);
      doc?.removeEventListener("keydown", cancel, true);
    };
  }, [cancel, rootRef]);

  const request = useCallback(
    (source: HTMLElement, afterDismiss: boolean) => {
      cancel();
      const generation = generationRef.current;
      const restore = () => {
        if (generation !== generationRef.current) return;
        frameRef.current = null;
        const root = rootRef.current;
        if (!root || isPlaybackSpaceBlocked(root, root)) return;
        const active = root.ownerDocument.activeElement;
        if (active !== source && active !== root.ownerDocument.body) return;
        if (active instanceof Element && active.matches(TEXT_ENTRY_SELECTOR)) return;
        focusTimeline();
      };
      // Click actions finish before the next key event. Outside-pointer dismiss must also wait
      // for the pointerdown's native focus and the popup's return-focus cleanup.
      if (afterDismiss) frameRef.current = requestAnimationFrame(restore);
      else queueMicrotask(restore);
    },
    [cancel, focusTimeline, rootRef],
  );
  const afterAction = useCallback((source: HTMLElement) => request(source, false), [request]);
  const afterDismiss = useCallback((source: HTMLElement) => request(source, true), [request]);
  return { afterAction, afterDismiss };
}
